import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { ChannelOption } from 'components/Wizards/CampaignWizard/Types'
import Styles from './DesignChannel.module.scss'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { useModals } from 'almanac-ui/Modal'
import { TemplatesModalV2 } from 'components/Templates/TemplatesModalV2/TemplatesModalV2'
import { DesignChannel as DesignChannelType } from 'components/NewFileModal/Types'
import { EditableName } from 'components/EditableName/EditableName'
import { useChannelsListContext } from './ChannelsListContext/ChannelsListContext'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Edit1, Trash2 } from 'blaze-ui/icons'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { GeneratedDesignPreview } from './GeneratedDesignPreview'
import { GeneratedArchetypePreview } from './GeneratedArchetypePreview/GeneratedArchetypePreview'
import { ConfirmModal } from 'blaze-components/ConfirmModal/ConfirmModal'

interface Props {
  channel: ChannelOption
}

export const DesignChannel = ({ channel }: Props) => {
  const { openModal, closeStack } = useModals()
  const { setEditingChannelType, editingChannelType, updateChannels, removeChannelType } = useChannelsListContext()

  const handlePress = async () => {
    if (!channel.width || !channel.height) return

    const hasExistingContent = channel.isNativeLayout || channel.source_visual_element_id || channel.content

    if (hasExistingContent) {
      const confirmed = await new Promise<boolean>((resolve) => {
        openModal(
          ConfirmModal,
          {
            title: 'Change Template',
            cancelText: 'Cancel',
            confirmText: 'Continue',
            prompt: 'The template will replace all existing content. Are you sure you want to proceed?',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
          },
          { newStack: true },
        )
      })

      if (!confirmed) return
    }

    openModal(
      TemplatesModalV2,
      {
        width: channel.width,
        height: channel.height,
        designChannel: channel.channel_type as DesignChannelType,
        onUse: async (templateId: number) => {
          updateChannels([
            {
              ...channel,
              source_visual_element_id: templateId,
              archetype: undefined,
              isNativeLayout: false,
            },
          ])
          closeStack()
        },
      },
      { newStack: true },
    )
  }

  return (
    <div className={Styles.designChannel}>
      <div className={Styles.header}>
        <ChannelIcon channel={channel.channel_type} size={20} />
        <EditableName
          autoFocus
          isDisabled
          name={channel.title ?? ''}
          forceOpen={editingChannelType === channel.channel_type}
          onClose={(newTitle) => {
            updateChannels([{ ...channel, title: newTitle }])
            setEditingChannelType(undefined)
          }}
          trigger={
            <Text variant="smallList" color="var(--dark-80)" lineClamp={1}>
              {channel.title}
            </Text>
          }
        />
        <ResponsiveDesign.Desktop>
          <div className={Styles.buttons}>
            <IconButton
              icon={Edit1}
              variant="ghost"
              size="xs"
              onPress={() => setEditingChannelType(channel.channel_type)}
              aria-label="Edit"
            />
            <IconButton
              icon={Trash2}
              size="xs"
              variant="ghost"
              onPress={() => removeChannelType(channel.channel_type)}
              aria-label="Remove"
            />
          </div>
        </ResponsiveDesign.Desktop>
      </div>
      {channel.isNativeLayout && channel.archetype ? (
        <GeneratedArchetypePreview channel={channel} />
      ) : (
        <GeneratedDesignPreview channel={channel} />
      )}
      <div className={Styles.footer}>
        <Button size="xs" variant="ghost" onPress={handlePress}>
          Pick another template
        </Button>
      </div>
    </div>
  )
}
